import Tenant from "entities/Tenant";
import { useEffect, useMemo, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getTenants, getTenantCompanies } from "store/admin/tenantWideData";
import { AppDispatch, AppState } from "store/configureStore";
import { roles } from "util/config/roles";

interface TenantCompanySelectorProps {
  generalCompanyOption?: boolean;
  selectOnlyTenant?: boolean;
  onSelectionChange: (tenantId: number | null, companyId: number | null) => void;
}

const TenantCompanySelector: React.FC<TenantCompanySelectorProps> = ({ onSelectionChange }) => {
  const { t } = useTranslation("parts");
  const dispatch = useDispatch<AppDispatch>();
  const role = roles();

  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const userTenantId = useSelector((state: AppState) => state.users.auth.data?.TenantId);

  const {list: tenants, loading: tenantsLoading} = useSelector((state: AppState) => state.admin.tenantWideData.tenants);
  
  useEffect(() => {
    dispatch(getTenants());
  }, [dispatch]);

  const displayTenants =useMemo(() => {
    if (role.isSuperAdmin) {
      return tenants.filter(t => t.Id !== 1);
    }
    return tenants;
  }, [tenants, role.isSuperAdmin]);

  useEffect(() => {
    if (tenants.length === 0) {
      return;
    }

    if (role.isAdmin && !role.isSuperAdmin) {
      if (userTenantId) {
        setSelectedTenantId(userTenantId);
        onSelectionChange(userTenantId, 0);
      }
      return;
    }

    if (selectedTenantId === null) {
      const userTenantExists = displayTenants.some(t => t.Id === userTenantId);

      if (userTenantId && userTenantExists) {
        setSelectedTenantId(userTenantId);
        onSelectionChange(userTenantId, 0);
      } else if (displayTenants.length === 1) {
        setSelectedTenantId(displayTenants[0].Id);
        onSelectionChange(displayTenants[0].Id, 0);
      }
    }
  }, [displayTenants, role.isAdmin, role.isSuperAdmin, userTenantId, onSelectionChange, selectedTenantId]);

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tenantId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedTenantId(tenantId);
    onSelectionChange(tenantId, 0);
  };

  return (
    <Row>
      <Form.Group as={Row} controlId="formTenantSelect" className="mb-3">
        <Col sm={8}>
          <Form.Select
            aria-label="Tenant selection"
            value={selectedTenantId ?? ""}
            onChange={handleTenantChange}
            disabled={tenantsLoading || (role.isAdmin && !role.isSuperAdmin)}
          >
            <option value="" disabled>{tenantsLoading ? "Loading..." :  "--- " + t("settings.selectTenant") + " ---"}</option>
            {displayTenants.map((tenant: Tenant) => (
                <option key={tenant.Id} value={tenant.Id}>
                    {tenant.Name}
                </option>
            ))}
          </Form.Select>
        </Col>
        <Form.Label column sm={4}>{t("settings.selectTenant")}</Form.Label>
      </Form.Group>
    </Row>
  );
};

export default TenantCompanySelector;